import { Box, CircularProgress, Typography } from '@mui/material';
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CREATE_ORGANIZATION, UPDATE_ORGANIZATION } from '../../constants';
import { IdName } from '../../models/global';
import { State } from '../../models/state';
import {
  createOrganization,
  getCountries,
  getOrganization,
  updateOrganization,
} from '../../store/actions/organization';
import { getStaticValues } from '../../store/actions/survey';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import Switch from '../FormComponents/Switch';
import TextField from '../FormComponents/TextField';
import NoData from '../NoData/NoData';
import Table from '../Table/Table';
import ToastMessage from '../ToastMessage/ToastMessage';

interface OrganizationFormProps {
  type: string;
}

function OrganizationForm(props: OrganizationFormProps) {
  const { type } = props;
  const { organizationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [organizationName, setOrganizationName] = useState('');
  const [country, setCountry] = useState<IdName>();
  const [language, setLanguage] = useState('');
  const [parallelFilling, setParallelFilling] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [formClicked, setFormClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const { countryList } = useSelector((state: State) => state.organization);
  const { languages } = useSelector((state: State) => state.survey);

  const getLanguages = () => {
    dispatch(getStaticValues());
  };

  const getCountryList = () => {
    dispatch(getCountries());
  };

  const getData = () => {
    setPageLoading(true);
    dispatch(
      getOrganization(organizationId, res => {
        setPageLoading(false);
        if (!res) {
          setNotFound(true);
        } else {
          setOrganizationName(res.name);
          setCountry(res.country);
          setLanguage(res.language);
          setOrganizationUsers(res.users);
          setParallelFilling(res.parallelFilling);
        }
      }),
    );
  };

  useEffect(() => {
    getCountryList();
    getLanguages();
    if (type === UPDATE_ORGANIZATION) {
      if (location?.state?.message) {
        setToastType('success');
        setToast(location?.state?.message);
        window.history.replaceState({}, '');
      }
      getData();
    }
  }, []);

  const handleFormClick = () => {
    setFormClicked(true);
    if (!organizationName || !country || !language) {
      return;
    }
    setLoading(true);
    if (type === CREATE_ORGANIZATION) {
      dispatch(
        createOrganization(
          {
            name: organizationName,
            countryId: country.id,
            language,
            parallelFilling,
          },
          (res, message) => {
            setLoading(false);
            if (res) {
              navigate('/organizations', {
                state: {
                  message: 'Organization is created.',
                },
              });
            } else {
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    } else {
      dispatch(
        updateOrganization(
          {
            id: organizationId,
            name: organizationName,
            countryId: country.id,
            language,
            parallelFilling,
          },
          (res, message) => {
            setLoading(false);
            if (res) {
              navigate('/organizations', {
                state: {
                  message: 'Organization is updated.',
                },
              });
            } else {
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    }
  };

  const handleParallelFilling = (value: boolean) => {
    setParallelFilling(value);
  };

  const goToCreateUser = () => {
    if (organizationId) {
      navigate(`/users/create?organizationId=${organizationId}`);
    } else {
      navigate('/users/create');
    }
  };

  const userColumns = ['Name', 'Email', 'Position', 'Language', 'Role', 'Active'];

  if (type === UPDATE_ORGANIZATION && notFound) {
    return <NoData message='No organization found' />;
  }
  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <>
      <Box>
        <Typography variant='h5'>Organization {type === CREATE_ORGANIZATION ? 'creation' : 'details'}</Typography>
        <Box display='flex' gap={4} marginTop={2}>
          <TextField
            label='Name'
            id='name'
            variant='outlined'
            sx={{ width: '30%' }}
            value={organizationName}
            onChange={e => setOrganizationName(e.target.value)}
            required
            error={formClicked && !organizationName}
          />
          <Dropdown
            value={country || ''}
            label='Country'
            options={countryList}
            onChange={(_: SyntheticEvent, value) => setCountry(value as IdName)}
            required
            error={formClicked && !country}
          />
          <Dropdown
            value={language}
            label='Language'
            options={languages}
            onChange={(_: SyntheticEvent, value: string) => setLanguage(value)}
            required
            error={formClicked && !language}
          />
          <Switch
            label='Parallel filling (beta)'
            checked={!!parallelFilling}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleParallelFilling(e.target.checked)}
          />
        </Box>
        <Box className='submit-button'>
          <Button
            text={type === CREATE_ORGANIZATION ? 'Create' : 'Update'}
            onClick={handleFormClick}
            loading={loading}
          />
        </Box>
      </Box>
      {type === UPDATE_ORGANIZATION && (
        <Box>
          <Box mt={5} mb={2} display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>Users in the organization</Typography>
            <Button text='New user' onClick={goToCreateUser} />
          </Box>
          <Table
            columns={userColumns}
            rows={organizationUsers}
            page={1}
            rowClickHandler={(id: string) => navigate(`/users/${id}`)}
            // actionIcons={[
            //   {
            //     title: 'Set as primary user',
            //     icon: <Radio checked={false} />,
            //     onClick: (id) => handlePrimaryUser(id),
            //   },
            // ]}
          />
        </Box>
      )}
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default OrganizationForm;
